import React, { useState, useEffect } from 'react';
import { Box, Typography, Button, TextField, LinearProgress } from '@mui/material';
import SettingsIcon from '@mui/icons-material/TuneOutlined';
import { textFieldStyles, formControlStyles } from '../styles';
import { getAccountByEmail, updateAccount } from '../services/accountService';
import CONFIG from '../config/config';

const Account = () => {
    const [isEdit, setIsEdit] = useState(false);
    const [isDirty, setIsDirty] = useState(false);
    const [accountData, setAccountData] = useState(null);
    const [originalData, setOriginalData] = useState(null);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(true);

    const accountGuid = localStorage.getItem('authSession')
        ? JSON.parse(localStorage.getItem('authSession')).accountGuid
        : null;

    const email = localStorage.getItem('authSession')
        ? JSON.parse(localStorage.getItem('authSession')).email
        : null;

    useEffect(() => {
        const fetchAccountData = async () => {
            if (!email) {
                setError('No email found in local storage.');
                setLoading(false);
                return;
            }

            try {
                const account = await getAccountByEmail(email);
                if (!account || !account.accountGuid) {
                    throw new Error('Account record not found or invalid.');
                }

                setAccountData({
                    accountGuid: account.accountGuid || '',
                    accountName: account.accountName || '',
                    contact: account.contact || '',
                    email: account.email || '',
                    phoneNumber: account.phoneNumber || '',
                });
                setOriginalData({
                    accountGuid: account.accountGuid || '',
                    accountName: account.accountName || '',
                    contact: account.contact || '',
                    email: account.email || '',
                    phoneNumber: account.phoneNumber || '',
                });
            } catch (err) {
                console.error('Error fetching account data:', err.message);
                setError('Failed to load account data. Please try again.');
            } finally {
                setLoading(false);
            }
        };

        fetchAccountData();
    }, [email]);

    const handleEditClick = () => {
        setIsEdit(true);
    };

    const handleCancel = () => {
        setIsEdit(false);
        setAccountData({ ...originalData }); // Revert changes
        setIsDirty(false); // Reset dirty state
    };

    const handleSave = async () => {
        if (!accountData?.accountGuid) {
            console.error('No accountGuid available for update');
            return;
        }

        const modifiedFields = {};

        // Compare the original data and the current data to determine modified fields
        Object.keys(originalData).forEach((key) => {
            if (originalData[key] !== accountData[key]) {
                modifiedFields[key] = {
                    value: accountData[key],
                    timestamp: new Date().toISOString(),
                };
            }
        });

        try {
            await updateAccount(accountData.accountGuid, {
                modifiedFields,
                version: 1, // Replace with dynamic version if required
            });
            setOriginalData({ ...accountData }); // Save the updated data as the new original
            setIsEdit(false);
            setIsDirty(false);
            console.log('Account updated successfully');
        } catch (err) {
            console.error('Error updating account:', err.message);
            setError('Failed to update account. Please try again.');
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setAccountData((prev) => ({
            ...prev,
            [name]: value,
        }));
        setIsDirty(true); // Mark form as dirty
    };

    if (loading) {
        return (
            <Box sx={{ width: '100%' }}>

            </Box>
        );
    }

    if (error) {
        return (
            <Box sx={{ padding: 4, textAlign: 'center' }}>
                <Typography variant="h6" color="error">
                    {error}
                </Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ padding: 0 }}>
            {/* Header Row */}
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: 6,
                    paddingLeft: '16px',
                    paddingRight: '16px',
                }}
            >
                {/* Left-aligned content */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <SettingsIcon sx={{ fontSize: '20px', color: '#2c3e50' }} />
                    <Typography
                        variant="h5"
                        fontWeight="bold"
                        sx={{ color: '#2c3e50' }}
                    >
                        Account Settings
                    </Typography>
                </Box>
            </Box>

            {/* Horizontal Menu */}
            <Box
                sx={{
                    px: 2,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: 2,
                }}
            >
                <Typography variant="body1" sx={{ color: '#2c3e50' }}>
                    Holder
                </Typography>
                <Button
                    variant="contained"
                    onClick={handleEditClick}
                    sx={{
                        alignSelf: 'center',
                    }}
                >
                    Edit
                </Button>
            </Box>
            {/* Horizontal Line */}
            <Box sx={{ px: 2 }}>
                <Box sx={{ borderBottom: '1px solid #dcdcdc', width: '100%' }} />
            </Box>

            {/* Page Content */}
            <Box sx={{ px: 4, py: 3 }}>
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        gap: 4,
                    }}
                >
                    {/* Left Column */}
                    <Box sx={{ flex: 1 }}>
                        {/* Account Name Field */}
                        <Box sx={{ marginBottom: 2 }}>
                            <Typography
                                variant="subtitle1"
                                sx={{ fontWeight: 500, color: '#2c3e50', fontSize: '1.25rem' }}
                            >
                                Account Name
                            </Typography>
                            {isEdit ? (
                                <TextField
                                    fullWidth
                                    name="name"
                                    value={accountData.accountName}
                                    onChange={handleChange}
                                    margin="normal"
                                    autoComplete='off'
                                    sx={textFieldStyles}
                                />
                            ) : (
                                <Typography sx={{ color: '#6c757d' }}>
                                    {accountData.accountName}
                                </Typography>
                            )}
                        </Box>

                        {/* Contact Field */}
                        <Box sx={{ marginBottom: 2 }}>
                            <Typography
                                variant="subtitle1"
                                sx={{ fontWeight: 500, color: '#2c3e50', fontSize: '1.25rem' }}
                            >
                                Contact Person
                            </Typography>
                            {isEdit ? (
                                <TextField
                                    fullWidth
                                    name="name"
                                    value={accountData.contact}
                                    onChange={handleChange}
                                    margin="normal"
                                    autoComplete='off'
                                    sx={textFieldStyles}
                                />
                            ) : (
                                <Typography sx={{ color: '#6c757d' }}>
                                    {accountData.contact}
                                </Typography>
                            )}
                        </Box>
                    </Box>

                    {/* Right Column */}
                    <Box sx={{ flex: 1 }}>
                        {/* Email Field */}
                        <Box sx={{ marginBottom: 2 }}>
                            <Typography
                                variant="subtitle1"
                                sx={{ fontWeight: 500, color: '#2c3e50', fontSize: '1.25rem' }}
                            >
                                Email
                            </Typography>
                            {isEdit ? (
                                <TextField
                                    fullWidth
                                    name="email"
                                    value={accountData.email}
                                    //onChange={handleChange}
                                    margin="normal"
                                    autoComplete='off'
                                    sx={textFieldStyles}
                                    InputProps={{
                                        readOnly: true,
                                    }}
                                />
                            ) : (
                                <Typography sx={{ color: '#6c757d' }}>
                                    {accountData.email}
                                </Typography>
                            )}
                        </Box>

                        {/* Phone Field */}
                        <Box sx={{ marginBottom: 2 }}>
                            <Typography
                                variant="subtitle1"
                                sx={{ fontWeight: 500, color: '#2c3e50', fontSize: '1.25rem' }}
                            >
                                Phone
                            </Typography>
                            {isEdit ? (
                                <TextField
                                    fullWidth
                                    name="phoneNumber"
                                    value={accountData.phoneNumber}
                                    onChange={handleChange}
                                    margin="normal"
                                    autoComplete='off'
                                    sx={textFieldStyles}
                                />
                            ) : (
                                <Typography sx={{ color: '#6c757d' }}>
                                    {accountData.phoneNumber}
                                </Typography>
                            )}
                        </Box>
                    </Box>
                </Box>
            </Box>

            {/* Pinned Bar */}
            {isEdit && (
                <Box
                    sx={{
                        position: 'fixed',
                        bottom: 0,
                        left: `${CONFIG.MEASURE.SIDEBAR_WIDTH}px`,
                        right: 0,
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '8px 16px',
                        backgroundColor: isDirty ? '#ffec9e' : '#f9f9f9',
                        borderTop: '1px solid #dcdcdc',
                        boxShadow: '0 -2px 4px rgba(0, 0, 0, 0.1)',
                        zIndex: 999,
                    }}
                >
                    {/* Dirty State Message */}
                    <Typography
                        variant="body2"
                        sx={{
                            color: '#6c757d',
                            fontWeight: 400,
                            marginLeft: '16px', // Maintain alignment
                        }}
                    >
                        {isDirty ? 'Document modified.' : ''}
                    </Typography>

                    {/* Save/Cancel Buttons */}
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button variant="outlined" onClick={handleCancel}>
                            Cancel
                        </Button>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={handleSave}
                            disabled={!isDirty}
                        >
                            Save
                        </Button>
                    </Box>
                </Box>
            )}
        </Box>
    );
};

export default Account;